import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AutorenewIcon from "@material-ui/icons/Autorenew";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import NoteIcon from "@material-ui/icons/Note";
import NoteAddOutlinedIcon from "@material-ui/icons/NoteAddOutlined";
import EditIcon from "@material-ui/icons/Edit";

import PremiumValue from "../../components/PremiumValue";
import NoContactChip from "./NoContactChip";

import {
  expirationLabel,
  formatCurrency,
  formatDateOnly,
  hasRealPhone,
  isTerminalStage,
  stageBadgeColor,
  stageBadgeLabel,
  urgencyColor,
  urgencyLabel,
} from "./helpers";

const useStyles = makeStyles((theme) => ({
  body: {
    fontSize: 12,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
  },
  headerRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
  },
  notesToggle: {
    padding: 2,
    marginTop: -4,
    marginRight: -4,
  },
  notesIconFilled: {
    color: "#2E7D32",
  },
  notesIconEmpty: {
    color: theme.palette.text.secondary,
    opacity: 0.6,
  },
  notesBox: {
    marginTop: 6,
  },
  notesActions: {
    marginTop: 4,
    display: "flex",
    justifyContent: "flex-end",
    gap: 4,
  },
  urgency: {
    display: "inline-block",
    padding: "1px 6px",
    borderRadius: 3,
    color: "#FFF",
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  deadline: {
    marginLeft: 6,
    fontWeight: "bold",
  },
  line: {
    marginTop: 4,
    color: theme.palette.text.secondary,
  },
  lineRow: {
    marginTop: 4,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 4,
  },
  editToggle: {
    padding: 2,
  },
  kanbanFieldsEdit: {
    marginTop: 4,
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  noContact: {
    marginTop: 6,
  },
  renewal: {
    marginTop: 6,
    padding: "3px 6px",
    borderRadius: 3,
    backgroundColor: "rgba(46, 125, 50, 0.12)",
    color: "#2E7D32",
    fontSize: 11,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  renewalIcon: {
    fontSize: 14,
  },
  actions: {
    marginTop: 8,
    display: "flex",
    gap: 6,
  },
  action: {
    fontSize: 10,
    padding: "2px 6px",
    minWidth: 0,
    textTransform: "none",
  },
}));

/**
 * Conteúdo do card do Kanban. Vai no campo `description` do react-trello, que
 * aceita um nó React — mesmo caminho já usado pelo Kanban de tickets.
 *
 * A cor da faixa e o texto de prazo vêm de urgencyBucket/daysUntilExpiration,
 * calculados no servidor. Nada aqui reordena nem reclassifica card.
 */
const OpportunityCard = ({ opportunity, stage, onOpenTicket, onCreateContact, onSaveNotes, onSaveKanbanFields, canEditKanbanFields = false, canSetCelular = false }) => {
  const classes = useStyles();
  const { policy, contact, urgencyBucket, daysUntilExpiration, renewalPolicyId } = opportunity;

  // Em stage terminal a urgência da apólice antiga deixa de ser informação
  // útil e passa a confundir — ver TERMINAL_STAGES em helpers.js.
  const terminal = isTerminalStage(stage);

  // Anotação livre (Requisito B): um único texto por apólice, não histórico.
  // Colapsada por padrão; abre com o valor salvo, nunca perde o rascunho ao
  // salvar falhar (onSaveNotes devolve false e o card continua aberto).
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesDraft, setNotesDraft] = useState(policy?.notes || "");
  const [savingNotes, setSavingNotes] = useState(false);
  const hasNotes = Boolean(policy?.notes && policy.notes.trim());

  const handleToggleNotes = (event) => {
    event.stopPropagation();
    if (!notesOpen) setNotesDraft(policy?.notes || "");
    setNotesOpen((open) => !open);
  };

  const handleSaveNotes = async (event) => {
    event.stopPropagation();
    setSavingNotes(true);
    const trimmed = notesDraft.trim();
    const ok = await onSaveNotes(policy.id, trimmed ? notesDraft : null);
    setSavingNotes(false);
    if (ok) setNotesOpen(false);
  };

  // Edição de Seguradora/Valor (Requisito A): sem restrição por stage, vale
  // em qualquer coluna, inclusive estágios terminais — ver investigação do
  // Bloco 2 (não havia edição nenhuma desses campos antes, não há o que
  // desbloquear). Mesmo padrão de rascunho local de handleSaveNotes.
  const [kanbanFieldsOpen, setKanbanFieldsOpen] = useState(false);
  const [insurerDraft, setInsurerDraft] = useState(policy?.insurer || "");
  const [premiumDraft, setPremiumDraft] = useState(
    policy?.premium !== null && policy?.premium !== undefined ? String(policy.premium) : ""
  );
  const [savingKanbanFields, setSavingKanbanFields] = useState(false);

  const handleToggleKanbanFields = (event) => {
    event.stopPropagation();
    if (!kanbanFieldsOpen) {
      setInsurerDraft(policy?.insurer || "");
      setPremiumDraft(policy?.premium !== null && policy?.premium !== undefined ? String(policy.premium) : "");
    }
    setKanbanFieldsOpen((open) => !open);
  };

  const handleSaveKanbanFields = async (event) => {
    event.stopPropagation();
    setSavingKanbanFields(true);
    const ok = await onSaveKanbanFields(policy.id, {
      insurer: insurerDraft,
      premium: premiumDraft.trim() === "" ? null : Number(premiumDraft),
    });
    setSavingKanbanFields(false);
    if (ok) setKanbanFieldsOpen(false);
  };

  // Card sem contato é o estado normal de apólice recém-importada: o Kanban
  // não espera a conciliação para existir. O selo diz o que falta.
  const hasContact = Boolean(contact && contact.id);
  const canTalk = hasRealPhone(contact);

  // Oferece o cadastro em dois casos: (1) apólice sem conciliação nenhuma —
  // ex.: as migradas sem PolicyImportStaging, caminho novo via
  // PUT /contatos/celular direto por CPF/CNPJ — ou (2) registro de
  // conciliação ainda "pending". Um registro "ignorado" na tela de
  // Conciliação não tem mais candidato nenhum a oferecer aqui — chamar
  // create-contact sobre ele falharia no backend ("Este registro já foi
  // resolvido"), então nem mostramos o botão. Qual dos dois caminhos abrir
  // (modal de staging antigo vs. SetCelularModal novo) é decidido pelo
  // mesmo onCreateContact, olhando policy.importStaging.
  // Sem importStaging o caminho é o SetCelularModal (PUT /contatos/celular,
  // isAdmin): só admin vê o botão. Com importStaging pendente (Cadastrar
  // contato) continua como antes, para quem já via.
  const canCreateContact =
    !hasContact &&
    (policy?.importStaging ? policy.importStaging.status === "pending" : canSetCelular);

  return (
    <div className={classes.body}>
      <div className={classes.headerRow}>
        <div>
          {terminal ? (
            <span
              className={classes.urgency}
              style={{ backgroundColor: stageBadgeColor(stage) }}
            >
              {stageBadgeLabel(stage)}
            </span>
          ) : (
            <>
              <span
                className={classes.urgency}
                style={{ backgroundColor: urgencyColor(urgencyBucket) }}
              >
                {urgencyLabel(urgencyBucket)}
              </span>
              <span className={classes.deadline} style={{ color: urgencyColor(urgencyBucket) }}>
                {expirationLabel(daysUntilExpiration)}
              </span>
            </>
          )}
        </div>

        <Tooltip title={hasNotes ? "Ver/editar anotação" : "Adicionar anotação"}>
          <IconButton
            size="small"
            className={classes.notesToggle}
            onClick={handleToggleNotes}
            onMouseDown={(event) => event.stopPropagation()}
          >
            {hasNotes ? (
              <NoteIcon fontSize="small" className={classes.notesIconFilled} />
            ) : (
              <NoteAddOutlinedIcon fontSize="small" className={classes.notesIconEmpty} />
            )}
          </IconButton>
        </Tooltip>
      </div>

      {notesOpen ? (
        <div
          className={classes.notesBox}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <TextField
            multiline
            minRows={2}
            maxRows={6}
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Anotação sobre esta oportunidade..."
            value={notesDraft}
            onChange={(event) => setNotesDraft(event.target.value)}
            disabled={savingNotes}
          />
          <div className={classes.notesActions}>
            <Button size="small" className={classes.action} onClick={handleToggleNotes} disabled={savingNotes}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.action}
              onClick={handleSaveNotes}
              disabled={savingNotes}
            >
              {savingNotes ? <CircularProgress size={12} color="inherit" /> : "Salvar"}
            </Button>
          </div>
        </div>
      ) : null}

      {canEditKanbanFields && kanbanFieldsOpen ? (
        <div
          className={classes.kanbanFieldsEdit}
          onClick={(event) => event.stopPropagation()}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <TextField
            label="Seguradora"
            fullWidth
            variant="outlined"
            size="small"
            value={insurerDraft}
            onChange={(event) => setInsurerDraft(event.target.value)}
            disabled={savingKanbanFields}
          />
          <TextField
            label="Valor Total (R$)"
            fullWidth
            variant="outlined"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: 0 }}
            value={premiumDraft}
            onChange={(event) => setPremiumDraft(event.target.value)}
            disabled={savingKanbanFields}
          />
          <div className={classes.notesActions}>
            <Button size="small" className={classes.action} onClick={handleToggleKanbanFields} disabled={savingKanbanFields}>
              Cancelar
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              className={classes.action}
              onClick={handleSaveKanbanFields}
              disabled={savingKanbanFields}
            >
              {savingKanbanFields ? <CircularProgress size={12} color="inherit" /> : "Salvar"}
            </Button>
          </div>
        </div>
      ) : (
        <div className={classes.lineRow}>
          <span>
            {policy?.product} · {policy?.insurer} ·{" "}
            <PremiumValue value={policy?.premium} source={policy?.premiumSource} format={formatCurrency} />
          </span>
          {canEditKanbanFields ? (
            <Tooltip title="Editar seguradora/valor total">
              <IconButton
                size="small"
                className={classes.editToggle}
                onClick={handleToggleKanbanFields}
                onMouseDown={(event) => event.stopPropagation()}
              >
                <EditIcon fontSize="small" className={classes.notesIconEmpty} />
              </IconButton>
            </Tooltip>
          ) : null}
        </div>
      )}
      <div className={classes.line}>Vigência até {formatDateOnly(policy?.endDate)}</div>

      {!canTalk ? (
        <div className={classes.noContact}>
          <NoContactChip hasContact={hasContact} />
        </div>
      ) : null}

      {canCreateContact ? (
        <div className={classes.actions}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            className={classes.action}
            startIcon={<PersonAddIcon style={{ fontSize: 14 }} />}
            onClick={(event) => {
              event.stopPropagation();
              onCreateContact(opportunity);
            }}
          >
            Cadastrar contato
          </Button>
        </div>
      ) : null}

      {renewalPolicyId ? (
        <div className={classes.renewal}>
          <AutorenewIcon className={classes.renewalIcon} />
          Renovação detectada — confirme e mova para Renovada
        </div>
      ) : null}

      <div className={classes.actions}>
        <Tooltip
          title={
            canTalk
              ? "Abrir conversa no WhatsApp"
              : hasContact
              ? "Contato sem telefone real — complete o número primeiro"
              : "Apólice sem contato vinculado — não há com quem conversar ainda"
          }
        >
          <span>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              className={classes.action}
              startIcon={<WhatsAppIcon style={{ fontSize: 14 }} />}
              disabled={!canTalk}
              onClick={(event) => {
                event.stopPropagation();
                onOpenTicket(contact);
              }}
            >
              Conversar
            </Button>
          </span>
        </Tooltip>
      </div>
    </div>
  );
};

export default OpportunityCard;
